import { Container, Graphics } from 'pixi.js';
import type { AttackType } from '@/shared/types';
import type { CrumbleParticle } from './fighter-display';

/** A single particle in a hit effect. */
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: number;
  alpha: number;
  /** Rate at which the particle shrinks (0 = no shrink). */
  shrink: number;
  /** Gravity applied each frame. */
  gravity: number;
  /** Optional rotation for spark-type particles. */
  rotation: number;
  rotationSpeed: number;
  /** 'circle' for round blobs, 'spark' for elongated streaks. */
  shape: 'circle' | 'spark';
}

/** A shockwave ring that expands outward. */
interface Ring {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  life: number;
  maxLife: number;
  color: number;
  lineWidth: number;
}

/** One active hit effect instance. */
export interface HitEffect {
  particles: Particle[];
  rings: Ring[];
  /** Remaining screen-shake frames (special only). */
  shakeFrames: number;
  /** Current shake offset. */
  shakeX: number;
  shakeY: number;
  done: boolean;
}

// ─── Colour palettes per attack type ───────────────────────────

const BITE_COLORS = [0xff2222, 0xff4444, 0xcc0000, 0xff6633, 0xff0000];
const TAIL_COLORS = [0xff8800, 0xffaa00, 0xff6600, 0xffcc33, 0xffdd55];
const SPECIAL_COLORS = [0xff4400, 0xffaa00, 0xffff44, 0xff2200, 0xff6600, 0xffffff, 0xffdd00];

// ─── Factory helpers ───────────────────────────────────────────

function randomInRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function makeParticle(
  x: number,
  y: number,
  angle: number,
  speed: number,
  opts: Partial<Particle> = {},
): Particle {
  return {
    x,
    y,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    life: opts.life ?? 20,
    maxLife: opts.maxLife ?? opts.life ?? 20,
    size: opts.size ?? 4,
    color: opts.color ?? 0xff0000,
    alpha: opts.alpha ?? 1,
    shrink: opts.shrink ?? 0.02,
    gravity: opts.gravity ?? 0.1,
    rotation: opts.rotation ?? 0,
    rotationSpeed: opts.rotationSpeed ?? 0,
    shape: opts.shape ?? 'circle',
  };
}

// ─── Spawn functions per attack type ───────────────────────────

function spawnBiteEffect(x: number, y: number): HitEffect {
  const particles: Particle[] = [];

  // Core burst — small red blobs
  for (let i = 0; i < 10; i++) {
    const angle = randomInRange(-Math.PI, Math.PI);
    const speed = randomInRange(1.5, 4);
    particles.push(
      makeParticle(x, y, angle, speed, {
        life: Math.floor(randomInRange(12, 20)),
        size: randomInRange(2, 5),
        color: pick(BITE_COLORS),
        shrink: 0.04,
        gravity: 0.12,
        shape: 'circle',
      }),
    );
  }

  // A few short sparks
  for (let i = 0; i < 5; i++) {
    const angle = randomInRange(-Math.PI, Math.PI);
    const speed = randomInRange(3, 6);
    particles.push(
      makeParticle(x, y, angle, speed, {
        life: Math.floor(randomInRange(6, 12)),
        size: randomInRange(2, 3),
        color: pick(BITE_COLORS),
        shrink: 0.06,
        gravity: 0.05,
        shape: 'spark',
        rotation: angle,
        rotationSpeed: 0,
      }),
    );
  }

  // Small ring
  const rings: Ring[] = [
    {
      x,
      y,
      radius: 2,
      maxRadius: 20,
      life: 10,
      maxLife: 10,
      color: 0xff4444,
      lineWidth: 2,
    },
  ];

  return { particles, rings, shakeFrames: 0, shakeX: 0, shakeY: 0, done: false };
}

function spawnTailSwipeEffect(x: number, y: number): HitEffect {
  const particles: Particle[] = [];

  // Wider, lower arc — orange sweep
  for (let i = 0; i < 12; i++) {
    // Bias angle toward horizontal (± 60° from horizontal)
    const angle =
      randomInRange(-Math.PI * 0.35, Math.PI * 0.35) + (Math.random() > 0.5 ? 0 : Math.PI);
    const speed = randomInRange(2, 5.5);
    particles.push(
      makeParticle(x, y, angle, speed, {
        life: Math.floor(randomInRange(14, 22)),
        size: randomInRange(3, 6),
        color: pick(TAIL_COLORS),
        shrink: 0.03,
        gravity: 0.15,
        shape: 'circle',
      }),
    );
  }

  // Dust sparks — horizontal streaks
  for (let i = 0; i < 6; i++) {
    const angle = randomInRange(-0.4, 0.4) + (Math.random() > 0.5 ? 0 : Math.PI);
    const speed = randomInRange(3, 7);
    particles.push(
      makeParticle(x, y, angle, speed, {
        life: Math.floor(randomInRange(8, 14)),
        size: randomInRange(2, 4),
        color: pick(TAIL_COLORS),
        shrink: 0.05,
        gravity: 0.08,
        shape: 'spark',
        rotation: angle,
        rotationSpeed: 0,
      }),
    );
  }

  // Low ring
  const rings: Ring[] = [
    {
      x,
      y,
      radius: 3,
      maxRadius: 28,
      life: 12,
      maxLife: 12,
      color: 0xff8800,
      lineWidth: 2.5,
    },
  ];

  return { particles, rings, shakeFrames: 0, shakeX: 0, shakeY: 0, done: false };
}

function spawnSpecialEffect(x: number, y: number): HitEffect {
  const particles: Particle[] = [];

  // Massive burst — lots of particles
  for (let i = 0; i < 24; i++) {
    const angle = randomInRange(-Math.PI, Math.PI);
    const speed = randomInRange(2, 8);
    particles.push(
      makeParticle(x, y, angle, speed, {
        life: Math.floor(randomInRange(18, 30)),
        size: randomInRange(3, 8),
        color: pick(SPECIAL_COLORS),
        shrink: 0.025,
        gravity: 0.08,
        shape: 'circle',
      }),
    );
  }

  // Flying sparks — long streaks in all directions
  for (let i = 0; i < 12; i++) {
    const angle = randomInRange(-Math.PI, Math.PI);
    const speed = randomInRange(5, 11);
    particles.push(
      makeParticle(x, y, angle, speed, {
        life: Math.floor(randomInRange(10, 18)),
        size: randomInRange(2, 4),
        color: pick(SPECIAL_COLORS),
        shrink: 0.04,
        gravity: 0.04,
        shape: 'spark',
        rotation: angle,
        rotationSpeed: randomInRange(-0.1, 0.1),
      }),
    );
  }

  // Central bright flash (large short-lived particle)
  particles.push(
    makeParticle(x, y, 0, 0, {
      life: 8,
      size: 18,
      color: 0xffffff,
      shrink: 0.12,
      gravity: 0,
      alpha: 0.9,
      shape: 'circle',
    }),
  );

  // Multiple expanding rings
  const rings: Ring[] = [
    {
      x,
      y,
      radius: 3,
      maxRadius: 45,
      life: 14,
      maxLife: 14,
      color: 0xffaa00,
      lineWidth: 3,
    },
    {
      x,
      y,
      radius: 2,
      maxRadius: 30,
      life: 10,
      maxLife: 10,
      color: 0xffffff,
      lineWidth: 2,
    },
    {
      x,
      y,
      radius: 4,
      maxRadius: 55,
      life: 18,
      maxLife: 18,
      color: 0xff4400,
      lineWidth: 2,
    },
  ];

  return { particles, rings, shakeFrames: 6, shakeX: 0, shakeY: 0, done: false };
}

// ─── Public API ────────────────────────────────────────────────

/**
 * Create a new hit effect at the given position for the given attack type.
 */
export function createHitEffect(x: number, y: number, attackType: AttackType): HitEffect {
  switch (attackType) {
    case 'bite':
      return spawnBiteEffect(x, y);
    case 'tail-swipe':
      return spawnTailSwipeEffect(x, y);
    case 'special':
      return spawnSpecialEffect(x, y);
    default:
      return spawnBiteEffect(x, y);
  }
}

/**
 * Advance a hit effect by one frame. Returns true when the effect is finished.
 */
export function updateHitEffect(effect: HitEffect): boolean {
  let alive = false;

  // Update particles
  for (const p of effect.particles) {
    if (p.life <= 0) continue;
    p.life--;
    p.x += p.vx;
    p.y += p.vy;
    p.vy += p.gravity;
    p.vx *= 0.97; // drag
    p.size = Math.max(0, p.size - p.shrink);
    p.alpha = (p.life / p.maxLife) * 0.9 + 0.1;
    p.rotation += p.rotationSpeed;
    if (p.life > 0 && p.size > 0.2) alive = true;
  }

  // Update rings
  for (const r of effect.rings) {
    if (r.life <= 0) continue;
    r.life--;
    const t = 1 - r.life / r.maxLife;
    r.radius = r.maxRadius * t;
    if (r.life > 0) alive = true;
  }

  // Screen shake
  if (effect.shakeFrames > 0) {
    effect.shakeFrames--;
    const intensity = effect.shakeFrames * 1.2;
    effect.shakeX = (Math.random() - 0.5) * intensity;
    effect.shakeY = (Math.random() - 0.5) * intensity;
    alive = true;
  } else {
    effect.shakeX = 0;
    effect.shakeY = 0;
  }

  effect.done = !alive;
  return effect.done;
}

/**
 * Draw all active particles and rings of a hit effect into the given container
 * using a pooled Graphics object.
 */
export function drawHitEffect(effect: HitEffect, gfx: Graphics): void {
  // Draw rings first (behind particles)
  for (const r of effect.rings) {
    if (r.life <= 0) continue;
    const alpha = r.life / r.maxLife;
    gfx.circle(r.x, r.y, r.radius);
    gfx.stroke({ width: r.lineWidth * alpha, color: r.color, alpha: alpha * 0.8 });
  }

  // Draw particles
  for (const p of effect.particles) {
    if (p.life <= 0 || p.size < 0.3) continue;

    if (p.shape === 'circle') {
      gfx.circle(p.x, p.y, p.size);
      gfx.fill({ color: p.color, alpha: p.alpha });
    } else {
      // Spark: elongated line in direction of travel
      const len = p.size * 3;
      const cos = Math.cos(p.rotation);
      const sin = Math.sin(p.rotation);
      gfx.moveTo(p.x - cos * len, p.y - sin * len);
      gfx.lineTo(p.x + cos * len, p.y + sin * len);
      gfx.stroke({ width: p.size * 0.8, color: p.color, alpha: p.alpha });
    }
  }
}

// ─── Crumble particle helpers ──────────────────────────────────

const CRUMBLE_GRAVITY = 0.18;
const CRUMBLE_DRAG = 0.98;

/** Advance a crumble particle by one frame. Returns true when it's dead. */
function updateCrumbleParticle(p: CrumbleParticle): boolean {
  p.life--;
  p.x += p.vx;
  p.y += p.vy;
  p.vy += CRUMBLE_GRAVITY;
  p.vx *= CRUMBLE_DRAG;
  p.alpha = Math.max(0, p.life / p.maxLife);
  p.rotation += p.rotationSpeed;
  // Shrink slightly near end of life
  if (p.life < p.maxLife * 0.3) {
    p.size *= 0.97;
  }
  return p.life <= 0 || p.size < 0.5;
}

/** Draw a crumble particle as a rotated square block. */
function drawCrumbleParticle(p: CrumbleParticle, gfx: Graphics): void {
  if (p.life <= 0 || p.size < 0.5) return;

  const hs = p.size / 2;
  const cos = Math.cos(p.rotation);
  const sin = Math.sin(p.rotation);

  // Draw a rotated rectangle as a polygon (4 corners)
  const corners = [
    { x: -hs, y: -hs },
    { x: hs, y: -hs },
    { x: hs, y: hs },
    { x: -hs, y: hs },
  ];

  const transformed = corners.map((c) => ({
    x: p.x + c.x * cos - c.y * sin,
    y: p.y + c.x * sin + c.y * cos,
  }));

  gfx.moveTo(transformed[0].x, transformed[0].y);
  for (let i = 1; i < transformed.length; i++) {
    gfx.lineTo(transformed[i].x, transformed[i].y);
  }
  gfx.closePath();
  gfx.fill({ color: p.color, alpha: p.alpha });

  // Darker edge to sell the blocky look
  gfx.moveTo(transformed[0].x, transformed[0].y);
  for (let i = 1; i < transformed.length; i++) {
    gfx.lineTo(transformed[i].x, transformed[i].y);
  }
  gfx.closePath();
  gfx.stroke({ width: 1, color: 0x000000, alpha: p.alpha * 0.4 });
}

/**
 * Manages a collection of active hit effects and the PixiJS container they render into.
 */
export class HitEffectManager {
  readonly container: Container;
  private effects: HitEffect[] = [];
  private crumbleParticles: CrumbleParticle[] = [];

  constructor() {
    this.container = new Container();
  }

  /** Spawn a new effect. */
  spawn(x: number, y: number, attackType: AttackType): void {
    this.effects.push(createHitEffect(x, y, attackType));
  }

  /** Add crumble particles (from a KO'd fighter). */
  spawnCrumble(particles: CrumbleParticle[]): void {
    this.crumbleParticles.push(...particles);
  }

  /** Advance all effects by one frame. Returns current screen-shake offset. */
  update(): { shakeX: number; shakeY: number } {
    let shakeX = 0;
    let shakeY = 0;

    for (const effect of this.effects) {
      updateHitEffect(effect);
      shakeX += effect.shakeX;
      shakeY += effect.shakeY;
    }

    // Remove finished effects
    this.effects = this.effects.filter((e) => !e.done);

    // Update crumble particles
    this.crumbleParticles = this.crumbleParticles.filter((p) => !updateCrumbleParticle(p));

    return { shakeX, shakeY };
  }

  /** Draw all active effects. Call once per frame after update(). */
  draw(): void {
    // Remove old graphics
    this.container.removeChildren();

    if (this.effects.length === 0 && this.crumbleParticles.length === 0) return;

    const gfx = new Graphics();

    for (const effect of this.effects) {
      drawHitEffect(effect, gfx);
    }

    // Draw crumble particles
    for (const p of this.crumbleParticles) {
      drawCrumbleParticle(p, gfx);
    }

    this.container.addChild(gfx);
  }

  /** Remove all active effects (e.g. on round transition). */
  clear(): void {
    this.effects = [];
    this.crumbleParticles = [];
    this.container.removeChildren();
  }

  /** Clean up PixiJS resources. */
  destroy(): void {
    this.container.destroy({ children: true });
  }
}
